import { useEffect, useState } from 'react';
import { FaTimes, FaInfoCircle, FaBug, FaTools, FaCircle, FaChevronDown } from 'react-icons/fa';
import changelogJson from '@/changelogs.json';

interface ChangelogItem {
  description: string;
  type: 'feature' | 'bugfix' | 'improvement';
}

export interface ChangelogInfo {
  title: string;
  datetime: string;
  updates: ChangelogItem[];
}

export default function Changelog() {
  const [isVisible, setIsVisible] = useState(false);
  const [unseenChangelogs, setUnseenChangelogs] = useState<[string, ChangelogInfo][]>([]);

  useEffect(() => {
    const allChangelogs = Object.entries(changelogJson) as [string, ChangelogInfo][];
    const sortedChangelogs = allChangelogs.sort((a, b) => {
      const versionA = a[0].split('.').map(Number);
      const versionB = b[0].split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if (versionA[i] !== versionB[i]) {
          return versionB[i] - versionA[i];
        }
      }
      return 0;
    });

    const lastSeenVersion = localStorage.getItem('changelog-last-seen');
    const unseenVersions = sortedChangelogs.filter(([version]) => {
      if (!lastSeenVersion) return true;
      
      const currentVer = version.split('.').map(Number);
      const lastVer = lastSeenVersion.split('.').map(Number);
      
      for (let i = 0; i < 3; i++) {
        if (currentVer[i] !== lastVer[i]) {
          return currentVer[i] > lastVer[i];
        }
      }
      return false;
    });

    if (unseenVersions.length > 0) {
      setUnseenChangelogs(unseenVersions);
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    if (unseenChangelogs.length > 0) {
      const latestVersion = unseenChangelogs[0][0];
      localStorage.setItem('changelog-last-seen', latestVersion);
    }
  };

  if (!isVisible || unseenChangelogs.length === 0) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4" onClick={handleClose}>
      <div className="max-w-lg w-full animate-slide-up max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/50 transition-all duration-300 hover:shadow-2xl">
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-bold bg-gradient-to-r from-bme-main to-[#e49756] bg-clip-text text-transparent">
                  Új frissítések
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-bme-main transition-colors"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="space-y-6">
              {unseenChangelogs.map(([version, changelog]) => (
                <div key={version} className="border-t border-gray-200/50 pt-4 first:border-t-0 first:pt-0">
                  <h4 className="text-gray-800 font-semibold text-base mb-4">
                    v{version} - {changelog.title}
                  </h4>

                  <div className="pl-4 space-y-4">
                    {changelog.updates.some(item => item.type === 'feature') && (
                      <details className="group" open>
                        <summary className="flex items-center justify-between cursor-pointer mb-2 text-blue-400">
                          <div className="flex items-center space-x-2 font-medium">
                            <FaInfoCircle className="text-base" />
                            <span>Új funkciók</span>
                          </div>
                          <FaChevronDown className="group-open:rotate-180 transition-transform duration-200" />
                        </summary>
                        <ul className="space-y-2 pl-4 mt-2">
                          {changelog.updates
                            .filter(item => item.type === 'feature')
                            .map((item, index) => (
                              <li key={index} className="flex items-start space-x-2 text-gray-600">
                                <FaCircle className="text-blue-400 text-[8px] mt-1.5 flex-shrink-0" />
                                <span className="text-sm">{item.description}</span>
                              </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {changelog.updates.some(item => item.type === 'bugfix') && (
                      <details className="group" open>
                        <summary className="flex items-center justify-between cursor-pointer mb-2 text-red-400">
                          <div className="flex items-center space-x-2 font-medium">
                            <FaBug className="text-base" />
                            <span>Hibajavítások</span>
                          </div>
                          <FaChevronDown className="group-open:rotate-180 transition-transform duration-200" />
                        </summary>
                        <ul className="space-y-2 pl-4 mt-2">
                          {changelog.updates
                            .filter(item => item.type === 'bugfix')
                            .map((item, index) => (
                              <li key={index} className="flex items-start space-x-2 text-gray-600">
                                <FaCircle className="text-red-400 text-[8px] mt-1.5 flex-shrink-0" />
                                <span className="text-sm">{item.description}</span>
                              </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {changelog.updates.some(item => item.type === 'improvement') && (
                      <details className="group" open>
                        <summary className="flex items-center justify-between cursor-pointer mb-2 text-green-400">
                          <div className="flex items-center space-x-2 font-medium">
                            <FaTools className="text-base" />
                            <span>Fejlesztések</span>
                          </div>
                          <FaChevronDown className="group-open:rotate-180 transition-transform duration-200" />
                        </summary>
                        <ul className="space-y-2 pl-4 mt-2">
                          {changelog.updates
                            .filter(item => item.type === 'improvement')
                            .map((item, index) => (
                              <li key={index} className="flex items-start space-x-2 text-gray-600">
                                <FaCircle className="text-green-400 text-[8px] mt-1.5 flex-shrink-0" />
                                <span className="text-sm">{item.description}</span>
                              </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
